import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import QAInput, { createQAInputOnSubmit } from '../components/QAInput';
import type { IHandleSubmitPayload } from '../components/QAInput';
import type { IQAInputRef } from '../components/QAInput/types';
import SingleQA from './components/SingleQA';
import useChatHistory from './hooks/useChatHistory';
import { useLocalState } from './hooks/useLocalState';
import { useChatPageTemplateConfig } from './hooks/useChatPageTemplateConfig';
import { isArray, isEmpty, last } from 'lodash-es';
import { useLocation, useHistory, useIntl } from 'umi';
import './index.scss';
import { useAutoReleaseScrollLock } from '../Home/hooks/useAutoReleaseScrollLock';
import scrollIcon from '@/pages/aigc/assets/svg/scroll-arrow.svg';
import { ChatShareDialog } from '@/pages/aigc/pages/Agent/ChatShare/types/chatShare';
import ChartShareModal from '@/pages/aigc/pages/Agent/ChatShare/ChatShareModal';
import { useMount, useSize } from 'ahooks';
import { buriedPointPage } from '@home/utils/buriedPoint';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useSearchParamsAppoint } from '@/pages/aigc/hooks/useQuery';
import { ChatDSTemplate, CHAT_DS_TEMPLATES } from '../ChatDSTemplate';
import EllipsisHover from '@gp/components/EllipsisHover';
import { ChartDSTempImg } from '../ChatShare/components/ChartDSTempImg';
import classNames from 'classnames';
import { useExternalQueryConfig } from '../Copilot/hooks/useExternalQueryConfig';
import {
  buildServerParams,
  buildFileListData,
  buildLogParams,
  filterConversationsByAction,
  getEffectiveSkills,
  getQuotedContent,
} from './utils/submitHelpers';
import { resolveModelQueryForSubmit } from '../components/QAInput/utils/inlineMentionLabel';
import { resolveChatPageFromPath } from '@/pages/aigc/utils/utils';
import { useSkillsData } from '../SkillsCollection/hooks';

export const ChatLandingPage = () => {
  const location = useLocation();
  const history = useHistory();
  const {
    state: {
      initialMessage,
      initFileList,
      from,
      model_id: initialModelId,
      locale,
      agentBanner,
      agentName,
      logParams: initialLogParams,
      serverParams: initialServerParams,
      selectedSkills: initialSelectedSkills,
      selectedCharts: initialSelectedCharts,
      selectedKnowledge: initialSelectedKnowledge,
    } = {} as any,
    query: { sessionId, chatMode, template } = {} as any,
  } = location || ({} as any);
  // 全局会话模式状态（用于识别历史 Skill 会话）
  const [sessionTemplateMode] = useLocalState<string>(
    '__dba_session_template_mode__',
    '',
  );
  const { formatMessage } = useIntl();
  const { fetchChatList, chatList, bookMarkedChats, refreshAfterChat } =
    useChatList({ manual: true });
  const {
    isDsMode,
    isExcel,
    isMagicPoster,
    isSkill,
    templateConfig,
    initSelectedSample,
  } = useChatPageTemplateConfig({
    template,
    sessionTemplateMode,
    sessionId,
    chatList,
    bookMarkedChats,
    agentName,
    agentBanner,
  });
  // 获取用户安装的 Skills（用于 allowed_skills）
  const { allowedSkillsForApi, allSkills } = useSkillsData();

  // const [isSubmitting, setIsSubmitting] = useState(false);
  const [editObj, setEditObj] = useState({});
  const [haveEdit, setHaveEdit] = useState(false);
  const chatPageRef = useRef<HTMLDivElement>(null);
  const qaInputRef = useRef<IQAInputRef>(null);
  // 当前分层，加这个size为了获取输入框的高度，解决文件上传向下箭头被遮盖问题，在QAinput组件内部调整状态，外层组件是感知不到的，故在上层加一个size
  const size = useSize(() => document.querySelector('.dba-chat-input-box'));
  const [showShareChatDialog, setShowShareChatDialog] =
    useState<ChatShareDialog>();
  const boxSize = useSize(document.querySelector('.dba-qa-input'));
  const boxWidth = boxSize?.width;
  const voiceIconWidth = boxWidth - 210;
  const voiceInitialActiveBar = Math.round(voiceIconWidth * (25 / 125));

  const {
    conversations,
    streamingState,
    startNewConversation,
    cancelRequest,
    fetchHistoryList,
    userInfo,
    dsReport,
    errorTipsMessage,
    handleLikeOrDislike,
    handleUpdateDsReport,
    retrying,
    loading,
  } = useChatHistory();

  // 外部配置读取 & QAInput props 计算（落地页只清空 store，不恢复外部配置）
  const { externalQueryInputProps } = useExternalQueryConfig({
    conversations,
    clearOnly: true,
  });

  // 只触发一次
  useEffect(() => {
    // 没有sessionId，则跳转到首页，硬性条件
    if (!sessionId) {
      history.push('/agent/home');
    }

    if (initialMessage || initFileList?.length > 0) {
      history.replace({
        pathname: '/agent/chat',
        query: {
          sessionId,
          chatMode,
          template,
        },
        state: {
          agentBanner,
          agentName,
          initialServerParams,
        },
      });
      // 首页点发送，会传递from（add），首页点推荐问题则为homeQuestion
      handleSubmit({
        value: initialMessage,
        fileList: [],
        options: {},
        from: from || '',
        deleteMessageIds: [],
        serverProps: initialServerParams,
        mentionConfig: {
          selectedSkills: initialSelectedSkills,
          selectedCharts: initialSelectedCharts,
          selectedKnowledge: initialSelectedKnowledge,
        },
      });
    } else {
      fetchHistoryList((res) => {
        // 修复安全问题：当用户访问他人的会话链接时，后端会返回空数据
        // 此时需要重定向到首页，避免用户停留在空白页面造成困惑
        if (isEmpty(res?.messages)) {
          history.push('/agent/home');
        }
      });
    }
  }, [sessionId]);

  useMount(() => {
    buriedPointPage({
      pageId: '700501',
      pageName: 'agentChat',
      extInfo: sessionId || '',
      pagePath: '/agent/chat',
      typeId: 'aigc',
    });
  });

  /**
   * 处理消息提交
   * @param payload - 提交对象入参（文案、文件、引用标签、来源等）
   */
  const handleSubmit = ({
    value,
    fileList: singleMessageFileList,
    options,
    from,
    deleteMessageIds,
    serverProps,
    mentionConfig,
    logParams: submitLogParams,
  }: IHandleSubmitPayload) => {
    const { selectedSkills, selectedCharts, selectedKnowledge } =
      mentionConfig ?? {};
    // 1. 确定实际使用的文件列表
    const fileList =
      (singleMessageFileList?.length ?? 0) > 0
        ? singleMessageFileList
        : initFileList;

    // 2. 获取 DS 报告模式的配置信息
    const dsReportChatConfig = isDsMode
      ? chatList?.find((task) => task?.session_id === sessionId) || {
        agent_model: '',
        system_language: '',
      }
      : null;

    // 2.5 Skill 会话中，根据 from 决定 skill 的生效策略（edit/refresh/其他）
    const { skills: effectiveSelectedSkills, isInherited: isSkillInherited } =
      getEffectiveSkills({
        selectedSkills,
        isSkillSession: isSkill,
        conversations,
        from,
        frontendMetadata: serverProps?.frontend_metadata,
      });

    // 2.6 知识库：展示 query（折叠）vs 模型 query（≥3 平铺）
    const displayQuery = value;
    const kbDocUnit = formatMessage({ id: 'dba_kb_inline_doc_unit' });
    const modelQuery = resolveModelQueryForSubmit(
      displayQuery,
      selectedKnowledge,
      kbDocUnit,
      serverProps?.frontend_metadata?.kb_expand_token as
        | { from: string; to: string; }
        | undefined,
    );

    // 2.7 解析 enrichedExternalQueryConfig（注入 quoted_content，）
    const enrichedExternalQueryConfig = getQuotedContent(
      conversations,
      null,
      from,
    );
    // 3. 构建服务器参数
    const serverParams = buildServerParams({
      userId: userInfo?.openid,
      modelId: serverProps?.model_id,
      mode: serverProps?.mode,
      externalQueryConfig: enrichedExternalQueryConfig,
      dsReportConfig: {
        isDsMode,
        agentModel: dsReportChatConfig?.agent_model,
        systemLanguage: dsReportChatConfig?.system_language,
        initialModelId,
        locale,
      },
      aiExcelConfig: {
        isExcel,
        metadata: serverProps?.metadata,
      },
      magicPosterConfig: {
        isMagicPoster,
      },
      skillConfig: {
        isSkill,
        selectedSkills: effectiveSelectedSkills,
        installedSkills: allowedSkillsForApi,
        allSkills,
        isInherited: isSkillInherited,
      },
      fileList,
      selectedCharts,
      selectedKnowledge,
      displayQuery,
      modelQuery,
      serverProps,
    });

    // 4. 构建日志参数
    const logParams = buildLogParams({
      dataSource: 'agent',
      initialLogParams,
      submitLogParams,
      dsReportConfig: {
        isDsMode,
        serverParams,
        agentName,
      },
      aiExcelConfig: {
        isExcel,
      },
      selectedSkills: effectiveSelectedSkills,
      fileList,
      selectedCharts,
      selectedKnowledge,
      kbDocUnit,
    });

    // 6. 过滤对话列表（编辑/刷新操作）
    const outConversationsList = filterConversationsByAction({
      from,
      deleteMessageIds,
      conversations,
    });

    // 7. 构建文件数据：serverParts 给后端，frontFileList 给本地 conversation
    //    编辑/刷新时通过 editContext 回灌原始 parts，buildFileListData 内部处理
    const { serverParts: fileListData, frontFileList } = buildFileListData(
      fileList,
      selectedCharts,
      enrichedExternalQueryConfig?.files,
      { from, deleteMessageIds, conversations },
    );
    // 8. 发起新对话
    startNewConversation(
      modelQuery,
      frontFileList,
      {
        ...options,
        onOpen: () => {
          fetchChatList();
        },
        onComplete: () => {
          refreshAfterChat();
          options?.onComplete?.();
        },
        onError: () => {
          options?.onError?.();
        },
      },
      {
        from,
        outConversationsList,
        logParams,
        serverParams,
        fileListData,
      },
    );
  };

  const { scrollToBottom, showDownArrow } = useAutoReleaseScrollLock({
    className: '.dba-chat-page',
    streamingState,
  });

  const isProcessing = ['pending', 'progressing'].includes(streamingState);
  const fromPath = resolveChatPageFromPath(dsReport);

  /** 猜你想问点击：走 QAInput 提交流程，携带当前 Skill / Chart */
  const handleMayAskRecommendClick = useCallback(
    (value: string, isWebSearch?: boolean): void => {
      scrollToBottom();
      qaInputRef.current?.submitText(value, {
        from: 'recommend',
        serverProps: { use_websearch: isWebSearch },
      });
    },
    [scrollToBottom],
  );

  /** QAInput 手动发送回调，适配为 ChatPage handleSubmit */
  const handleQAInputSubmit = useMemo(
    () =>
      createQAInputOnSubmit(handleSubmit, {
        beforeSubmit: scrollToBottom,
        deleteMessageIds: [],
      }),
    [handleSubmit, scrollToBottom],
  );

  return (
    <div className="dba-chat-box">
      <section className="dba-chat-page" ref={chatPageRef}>
        <div className="dba-chat-scroll">
          <div className="dba-chat-content">
            <ChartDSTempImg
              {...{
                title: !loading && (initSelectedSample?.agent_title as string),
                img: initSelectedSample?.agent_banner,
                className: classNames({
                  'dba-chat-content-ds-template': dsReport?.show,
                  'dba-chat-content-ds-template-list': template === 'dsReport',
                }),
              }}
            />
            {isArray(conversations) &&
              conversations?.map((conversation) => {
                const isLastItem =
                  last(conversations)?.messageId === conversation?.messageId;
                const fileData = conversation?.fileData;
                // const fileListData = fileData?.length > 0 ? {
                //   [conversation?.messageId]: fileData
                // } : fileList;
                const fileListResult =
                  fileData?.length > 0 ? fileData : conversation?.frontFileList;
                const fileListData = {
                  [conversation?.messageId]: fileListResult,
                };

                return (
                  <SingleQA
                    {...{ ...conversation, conversations }}
                    key={conversation.messageId}
                    question={conversation.query}
                    // isLoading={isLoading}
                    streamingState={streamingState}
                    isLastQA={isLastItem}
                    onRecommendClick={handleMayAskRecommendClick}
                    userInfo={userInfo}
                    editObj={editObj}
                    // fileData={fileData}
                    setEditObj={setEditObj}
                    setHaveEdit={setHaveEdit}
                    handleSubmit={handleSubmit}
                    scrollToBottom={scrollToBottom}
                    errorTipsMessage={errorTipsMessage}
                    isProcessing={isProcessing}
                    fileList={fileListData}
                    setShowShareChatDialog={setShowShareChatDialog}
                    handleLikeOrDislike={handleLikeOrDislike}
                    handleUpdateDsReport={handleUpdateDsReport}
                    dsReport={dsReport}
                    voiceActiveBar={voiceInitialActiveBar}
                    custVoiceStyle={{ width: `${voiceIconWidth}px` }}
                    from={fromPath}
                    retrying={retrying}
                  />
                );
              })}
          </div>
        </div>

        {showDownArrow && (
          <div
            className="dba-ic-arrow_down"
            onClick={scrollToBottom}
            style={{ bottom: size?.height + 10 }}
          >
            <img src={scrollIcon} alt="" className="dba-sub-nav-icon" />
          </div>
        )}
        {showShareChatDialog?.isShowCopyDialog && (
          <ChartShareModal
            showShareChatDialog={showShareChatDialog}
            setShowShareChatDialog={setShowShareChatDialog}
          />
        )}
      </section>
      <div className="dba-chat-input-box" id="dba-chat-input-container">
        <QAInput
          ref={qaInputRef}
          {...{
            state: streamingState,
            onSubmit: handleQAInputSubmit,
            sessionId,
            lastMessages: last(conversations),
            isProcessing,
            cancelRequest,
            retrying,
            className: classNames('dba-chat-input', {
              'dba-copilot-chat-input': dsReport?.show,
            }),
            from: fromPath,
            ...externalQueryInputProps,
            // onTextInputHeightChange: (height: number) => {
            //   const chatContent = chatPageRef.current;

            //   if (chatContent)
            //     chatContent.style.marginBottom = `${height + 96}px`;
            // },
          }}
          customHeader={
            (initSelectedSample?.agent_title || templateConfig.name) && (
              <div
                className={classNames('dba-qa-input-header', {
                  'dba-qa-input-header-expand': dsReport?.show,
                })}
              >
                <div className="dba-qa-input-header-text">
                  <EllipsisHover
                    text={
                      templateConfig.name ? (
                        <>
                          {templateConfig.icon && (
                            <img
                              src={templateConfig.icon}
                              alt=""
                              style={{
                                width: 20,
                                height: 20,
                                marginRight: 4,
                                verticalAlign: 'middle',
                              }}
                            />
                          )}
                          {templateConfig.name}
                        </>
                      ) : (
                        <>
                          {formatMessage({ id: 'dba_ds_report' })}【
                          {initSelectedSample?.agent_title}】
                        </>
                      )
                    }
                  />
                </div>
              </div>
            )
          }
          canChangeMode={!(template && template === 'dsReport')}
          disableDragUpload={haveEdit}
          voiceActiveBar={voiceInitialActiveBar}
          custVoiceStyle={{ width: `${voiceIconWidth}px` }}
          isCopilot={dsReport?.show}
        />
        <div className="dba-input-box-placeholder"></div>
      </div>
    </div>
  );
};

// eslint-disable-next-line react/display-name
export default () => {
  const searchParams = useSearchParamsAppoint() as {
    template?: string;
    sessionId?: string;
  };
  const { template, sessionId } = searchParams;
  if (
    CHAT_DS_TEMPLATES.includes(template as (typeof CHAT_DS_TEMPLATES)[number])
  ) {
    return <ChatDSTemplate key={sessionId || ''} />;
  }
  return <ChatLandingPage key={sessionId || ''} />;
};
